import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { CompanyProfileOnboarding as View } from '@views';
import { trackEvent } from '@utils/mixpanel';
import { SUCCESS, LOADING } from '@constants/requestPhase';
import { ONBOARDING_STEPS } from '@constants/common';
import { getCompanyById, editCompany } from '@store/company/duck';
import { getMetadata } from '@store/metadata/duck';

import { ReactComponent as X } from '@assets/svgs/X.svg';
import { ReactComponent as Facebook } from '@assets/svgs/FacebookProfile.svg';
import { ReactComponent as LinkedIn } from '@assets/svgs/LinkedInProfile.svg';
import { ReactComponent as Instagram } from '@assets/svgs/InstagramProfile.svg';

const CompanyProfileOnboarding = () => {
  const location = useLocation();

  const { user } = useSelector(store => store.userStore);

  const { onboardingState, companyById, editCompanyPhase, getCompanyByIdPhase, updateCompanyOnboardingPhase } =
    useSelector(store => store.companyStore);

  const { metadata } = useSelector(store => store.metadataStore);

  const [isCompanyProfileHidden, setIsCompanyProfileHidden] = useState(companyById.isHidden ?? false);
  const [aboutUs, setAboutUs] = useState(companyById.description ?? '');
  const [linkedinLink, setLinkedinLink] = useState(companyById.linkedin ?? '');
  const [instagramLink, setInstagramLink] = useState(companyById.instagram ?? '');
  const [facebookLink, setFacebookLink] = useState(companyById.facebook ?? '');
  const [twitterLink, setTwitterLink] = useState(companyById.twitter ?? '');
  const [industry, setIndustry] = useState(companyById.industry ?? '');
  const [website, setWebsite] = useState(companyById.website ?? '');

  const dispatch = useDispatch();

  const isAboutUsTheSame = aboutUs === (companyById.description ?? '');
  const isLinkedinTheSame = linkedinLink === (companyById.linkedin ?? '');
  const isInstagramTheSame = instagramLink === (companyById.instagram ?? '');
  const isFacebookTheSame = facebookLink === (companyById.facebook ?? '');
  const isTwitterTheSame = twitterLink === (companyById.twitter ?? '');
  const isIndustryTheSame = industry === (companyById.industry ?? '');
  const isWebsiteTheSame = website === (companyById.website ?? '');

  const resetValues = () => {
    setIsCompanyProfileHidden(companyById.isHidden ?? false);
    setAboutUs(companyById.description ?? '');
    setIndustry(companyById.industry ?? '');
    setLinkedinLink(companyById.linkedin ?? '');
    setInstagramLink(companyById.instagram ?? '');
    setTwitterLink(companyById.twitter ?? '');
    setFacebookLink(companyById.facebook ?? '');
    setWebsite(companyById.website ?? '');
  };

  useEffect(() => {
    const pageTitle = 'Mindspace: Company Profile';
    document.title = pageTitle;

    window.gtag('event', 'page_view', {
      page_title: pageTitle,
    });

    trackEvent('Page Viewed', {
      Page: 'Company Profile - Onboarding',
    });

    if (!Object.keys(metadata).length) {
      dispatch(getMetadata());
    }
  }, []);

  useEffect(() => {
    if (user?.company?.id) {
      dispatch(getCompanyById(user?.company?.id));
    }
  }, [user?.company?.id]);

  useEffect(() => {
    if (getCompanyByIdPhase === SUCCESS) {
      resetValues();
    }
  }, [getCompanyByIdPhase]);

  const handleAboutUsSave = e => {
    setAboutUs(e.target.value);
  };

  const handleIndustrySave = newIndustry => {
    setIndustry(prevIndustry => {
      if (prevIndustry === newIndustry) {
        return '';
      }
      return newIndustry;
    });
  };

  const handleCompanyProfileHiddenChangeState = () => {
    setIsCompanyProfileHidden(prevState => !prevState);
  };

  const handleWebsiteChange = e => {
    setWebsite(e.target.value);
  };

  const handleSave = () => {
    const companyData = {};

    if (!isAboutUsTheSame) {
      companyData.description = aboutUs;
    }
    if (!isLinkedinTheSame) {
      companyData.linkedin = linkedinLink;
    }
    if (!isInstagramTheSame) {
      companyData.instagram = instagramLink;
    }
    if (!isFacebookTheSame) {
      companyData.facebook = facebookLink;
    }
    if (!isTwitterTheSame) {
      companyData.twitter = twitterLink;
    }
    if (!isIndustryTheSame) {
      companyData.industry = industry;
    }
    if (!isWebsiteTheSame) {
      companyData.website = website;
    }
    companyData.isCompanyProfilePrivacyEnabled = isCompanyProfileHidden;

    dispatch(editCompany(companyById?.id, companyData));
  };

  const handleSavePhoto = useCallback(
    file => {
      const companyData = new FormData();

      companyData.append('logo', file);

      dispatch(editCompany(companyById?.id, companyData));
    },
    [companyById],
  );

  const handleRemovePhoto = useCallback(() => {
    dispatch(
      editCompany(companyById?.id, {
        logo: null,
      }),
    );
  }, [companyById]);

  const socials = useMemo(
    () => [
      {
        name: 'LinkedIn',
        value: linkedinLink,
        handler: e => {
          setLinkedinLink(e.target.value);
        },
        placeholder: 'linkedin.com/username',
        icon: LinkedIn,
      },
      {
        name: 'Instagram',
        value: instagramLink,
        handler: e => {
          setInstagramLink(e.target.value);
        },
        placeholder: 'instagram.com/username',
        icon: Instagram,
      },
      {
        name: 'X',
        value: twitterLink,
        handler: e => {
          setTwitterLink(e.target.value);
        },
        placeholder: 'x.com/username',
        icon: X,
      },
      {
        name: 'Facebook',
        value: facebookLink,
        handler: e => {
          setFacebookLink(e.target.value);
        },
        placeholder: 'facebook.com/username',
        icon: Facebook,
      },
    ],
    [linkedinLink, instagramLink, twitterLink, facebookLink],
  );

  const hasChanges = useMemo(
    () =>
      !isAboutUsTheSame ||
      !isWebsiteTheSame ||
      !isLinkedinTheSame ||
      !isInstagramTheSame ||
      !isFacebookTheSame ||
      !isTwitterTheSame ||
      !isIndustryTheSame,
    [
      isAboutUsTheSame,
      isWebsiteTheSame,
      isLinkedinTheSame,
      isInstagramTheSame,
      isFacebookTheSame,
      isTwitterTheSame,
      isIndustryTheSame,
    ],
  );

  return (
    <View
      isProofOfPaymentStepAvailable={onboardingState?.availableSteps?.includes(ONBOARDING_STEPS.PROOF_OF_PAYMENT)}
      isCompanyProfileHidden={isCompanyProfileHidden}
      onCompanyProfileHiddenChangeState={handleCompanyProfileHiddenChangeState}
      direction={location?.state?.direction}
      isPhotoLoading={editCompanyPhase === LOADING && updateCompanyOnboardingPhase !== LOADING}
      logo={companyById.logoUrl}
      onSavePhoto={handleSavePhoto}
      onRemovePhoto={handleRemovePhoto}
      hasChanges={hasChanges}
      onSave={handleSave}
      onAboutUsSave={handleAboutUsSave}
      onIndustrySave={handleIndustrySave}
      onWebsiteChange={handleWebsiteChange}
      companyName={companyById?.name}
      socials={socials}
      website={website}
      industryList={metadata.industries}
      userIndustry={industry}
      aboutUs={aboutUs}
    />
  );
};

export default CompanyProfileOnboarding;
